import React, { FC, memo, useMemo, useState } from 'react';
import { useParams } from 'react-router-dom';
import { ColumnsType } from 'antd/es/table';
import dayjs, { Dayjs } from 'dayjs';
import classNames from 'classnames';
import { useAppTranslation } from 'app/config/i18Config/hooks';
import { useGetWarehouseByIdQuery } from 'entities/Warehouse';
import {
  BoxHistoryActionNames,
  HistoryAction,
  HistoryActionTargetType,
  OpenValidationErrorActionNames,
  WarehouseHistoryActionNames,
  useGetPaginatedHistoryQuery,
} from 'entities/History';
import { actionTitleMapper } from 'widgets/HistoryActionRow';
import { ServerTable } from 'shared/ui/ServerTable';
import { ModernSelect as Select } from 'shared/ui/ModernSelect';
import { RangeDatePicker } from 'shared/ui/RangeDatePicker/RangeDatePicker';
import { getClientDateWithTimeFormat } from 'shared/utils/helpers/getDateFormat';
import { getLocalizedString } from 'shared/utils/helpers/JSONLocalization';

enum OpeningResult {
  REQUESTED = 'requested',
  OPENED = 'opened',
  CLOSED = 'closed',
  DENIED = 'denied',
}

// "attempt" is logged when the open command is sent to the lock,
// box open/close are confirmed later by the lock controller webhook
const ACTIONS_BY_RESULT: Record<OpeningResult, string[]> = {
  [OpeningResult.REQUESTED]: [WarehouseHistoryActionNames.door_open_attempt, BoxHistoryActionNames.box_open_attempt],
  [OpeningResult.OPENED]: [BoxHistoryActionNames.box_open],
  [OpeningResult.CLOSED]: [BoxHistoryActionNames.box_close],
  [OpeningResult.DENIED]: Object.values(OpenValidationErrorActionNames),
};

const RESULT_LABELS: Record<OpeningResult, string> = {
  [OpeningResult.REQUESTED]: 'Open request',
  [OpeningResult.OPENED]: 'Box opened',
  [OpeningResult.CLOSED]: 'Box closed',
  [OpeningResult.DENIED]: 'Denied',
};

const RESULT_CLASSES: Record<OpeningResult, string> = {
  [OpeningResult.REQUESTED]: 'text-primary bg-secondary',
  [OpeningResult.OPENED]: 'text-success bg-successLight',
  [OpeningResult.CLOSED]: 'text-primaryLight bg-secondaryLight',
  [OpeningResult.DENIED]: 'text-error bg-errorLight',
};

const getOpeningResult = (action: HistoryAction): OpeningResult =>
  (Object.keys(ACTIONS_BY_RESULT) as OpeningResult[]).find((result) => ACTIONS_BY_RESULT[result].includes(action.actionName)) ||
  OpeningResult.DENIED;

export const WarehouseHistoryPage: FC = memo(() => {
  const { warehouseId } = useParams();

  const { t } = useAppTranslation(['warehouses', 'profile']);

  const [targetType, setTargetType] = useState<Nullable<HistoryActionTargetType>>(null);
  const [result, setResult] = useState<Nullable<OpeningResult>>(null);
  const [dateFrom, setDateFrom] = useState<Nullable<Dayjs>>(null);
  const [dateTo, setDateTo] = useState<Nullable<Dayjs>>(null);

  const { data: warehouse } = useGetWarehouseByIdQuery({ warehouseId }, { skip: !warehouseId });

  const defaultFilters = useMemo(() => {
    return {
      warehouseId,
      actionName: (result ? ACTIONS_BY_RESULT[result] : Object.values(ACTIONS_BY_RESULT).flat()).join(','),
      targetType: targetType || [HistoryActionTargetType.WAREHOUSE, HistoryActionTargetType.BOX].join(','),
      startDate: dateFrom ? dateFrom.startOf('day').toISOString() : undefined,
      endDate: dateTo ? dateTo.endOf('day').toISOString() : undefined,
    };
  }, [warehouseId, result, targetType, dateFrom, dateTo]);

  const columns = useMemo<ColumnsType<HistoryAction>>(
    () => [
      {
        title: t('Date and time'),
        key: 'date',
        render: (_, record) => <div className="whitespace-nowrap">{getClientDateWithTimeFormat(record.createdAt)}</div>,
      },
      {
        title: t('Object'),
        key: 'object',
        render: (_, record) => {
          const { targetType: recordTargetType, boxName, targetName } = record.actionData;

          return (
            <div className="font-semibold">
              {recordTargetType === HistoryActionTargetType.BOX
                ? t('Box {{name}}', { name: boxName })
                : getLocalizedString(targetName) || t('Entrance door')}
            </div>
          );
        },
      },
      {
        title: t('User'),
        key: 'user',
        render: (_, record) => {
          const isOpenedWithoutRequest = record.actionName === BoxHistoryActionNames.box_open && !record.actionData.userName;

          return isOpenedWithoutRequest ? (
            <div className="text-error">{t('Opened without request from the app')}</div>
          ) : (
            <div className="text-primary">{record.actionData.userName || '-'}</div>
          );
        },
      },
      {
        title: t('Result'),
        key: 'result',
        render: (_, record) => {
          const openingResult = getOpeningResult(record);

          return (
            <div className="flex flex-col items-start gap-1">
              <span className={classNames('text-sm rounded-xl px-4 py-0.5 whitespace-nowrap', RESULT_CLASSES[openingResult])}>
                {t(RESULT_LABELS[openingResult])}
              </span>
              {openingResult === OpeningResult.DENIED && (
                <span className="text-sm text-primaryLight">{actionTitleMapper(record.actionName, record.actionData, t).action}</span>
              )}
            </div>
          );
        },
      },
    ],
    [t],
  );

  return (
    <div className="pt-7 space-y-5">
      <div className="text-2xl font-semibold">
        {t('Openings history')} <span className="text-accent text-2xl font-semibold">{warehouse?.name}</span>
      </div>

      <div className="flex flex-wrap items-end gap-4 bg-white p-4 rounded-lg mb-2">
        <Select
          containerClassName="w-[220px]"
          label={t('Object')}
          placeholder={t('All objects')}
          allowClear
          value={targetType}
          onChange={(value) => {
            setTargetType(value as HistoryActionTargetType);
          }}
          onClear={() => {
            setTargetType(null);
          }}
          options={[
            { value: HistoryActionTargetType.WAREHOUSE, label: t('Entrance doors') },
            { value: HistoryActionTargetType.BOX, label: t('Boxes') },
          ]}
        />
        <Select
          containerClassName="w-[220px]"
          label={t('Result')}
          placeholder={t('All results')}
          allowClear
          value={result}
          onChange={(value) => {
            setResult(value as OpeningResult);
          }}
          onClear={() => {
            setResult(null);
          }}
          options={Object.values(OpeningResult).map((value) => ({ value, label: t(RESULT_LABELS[value]) }))}
        />
        <RangeDatePicker
          label={t('Period')}
          placeholder={[t('From'), t('To')]}
          values={[dateFrom, dateTo]}
          onChangeFrom={(value) => {
            setDateFrom(value ? dayjs(value) : null);
          }}
          onChangeTo={(value) => {
            setDateTo(value ? dayjs(value) : null);
          }}
        />
      </div>

      <ServerTable
        columns={columns}
        fetchData={useGetPaginatedHistoryQuery}
        defaultFilters={defaultFilters}
        rowKey="actionId"
        pageSize={20}
      />
    </div>
  );
});
